import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { askAssistant, runAssistantAction, searchArticles } from '../api';
import { pluginText, pluginUrl } from '../plugins/pluginFrame';
import ArticleIcon from './ArticleIcon';
import ArticleLink, { externalUrlOf } from './ArticleLink';
import { articlePath } from '../utils/articlePath';

const EMPTY = { status: 'idle', results: [], tags: [] };

// How long typing has to pause before the search is asked: long enough that a
// word typed at speed is one request, not one per letter.
const SEARCH_DELAY_MS = 150;

/**
 * Search-as-you-type over articles and tags: a request once typing pauses for
 * SEARCH_DELAY_MS, cancelling the one still in flight, so only the newest
 * query's answer is ever shown, while the previous hits stay on screen until
 * it arrives. `status` is 'idle' for an empty query, then 'loading', 'done' or
 * 'error'.
 */
export function useArticleSearch(query) {
  const [state, setState] = useState(EMPTY);
  const trimmed = query.trim();

  useEffect(() => {
    if (!trimmed) {
      setState(EMPTY);
      return undefined;
    }

    setState((current) => ({ ...current, status: 'loading' }));
    const controller = new AbortController();

    const timer = window.setTimeout(() => {
      searchArticles(trimmed, { signal: controller.signal })
        .then((data) => setState({
          status: 'done',
          results: Array.isArray(data?.articles) ? data.articles : [],
          tags: Array.isArray(data?.tags) ? data.tags : [],
        }))
        .catch(() => {
          if (!controller.signal.aborted) setState({ ...EMPTY, status: 'error' });
        });
    }, SEARCH_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed]);

  return state;
}

// The marks the server puts around a hit in a text passage (search-query.ts).
const HIT_START = '\u0002';
const HIT_STOP = '\u0003';

/**
 * A passage of the article text the search found, its hits in <mark>: shown
 * for an article found by its text alone, so the reader sees why. Markdown
 * marks and line breaks are flattened - it is a glimpse, not the article.
 */
export function Snippet({ text }) {
  const flat = text.replace(/[#*`>|_]+/g, ' ').replace(/\s+/g, ' ').trim();
  const parts = flat.split(HIT_START);

  return (
    <span className="article-search-snippet">
      {'… '}
      {parts.map((part, i) => {
        if (i === 0) return part;
        const [hit, rest = ''] = part.split(HIT_STOP);
        return (
          <span key={i}>
            <mark>{hit}</mark>
            {rest}
          </span>
        );
      })}
      {' …'}
    </span>
  );
}

/** `text` with every case-insensitive occurrence of `query` in a <mark>. */
function Highlight({ text, query }) {
  const needle = query.trim().toLowerCase();
  if (!needle || !text) return text;

  const parts = [];
  const haystack = text.toLowerCase();
  let from = 0;
  let at = haystack.indexOf(needle);

  while (at !== -1) {
    if (at > from) parts.push(text.slice(from, at));
    parts.push(<mark key={at}>{text.slice(at, at + needle.length)}</mark>);
    from = at + needle.length;
    at = haystack.indexOf(needle, from);
  }

  if (from < text.length) parts.push(text.slice(from));
  return parts;
}

/** Opens a search hit the way its link would - for Enter in the search box. */
export function openSearchResult(article, navigate) {
  const url = externalUrlOf(article);
  if (url) {
    window.location.assign(url);
  } else {
    navigate(articlePath(article));
  }
}

/**
 * What "Fragen" / Enter does with the current search. A message the chat
 * claims (a question or a request rather than a search term, or anything once
 * a conversation is under way) goes to the chat; otherwise it opens the best
 * article, else the best tag, and only when nothing at all was found is the
 * query asked in the chat. Returns true when
 * the query was asked, so the caller can clear the field the way a sent
 * message would.
 */
export function submitSearch(query, search, navigate, chat) {
  const trimmed = query.trim();
  if (!trimmed) return false;

  if (chat.claims(trimmed)) {
    chat.ask(trimmed);
    return true;
  }

  if (search.status !== 'done') return false;

  const [article] = search.results;
  if (article) {
    openSearchResult(article, navigate);
    return false;
  }

  const [tag] = search.tags;
  if (tag) {
    navigate(`/tag/${encodeURIComponent(tag.name)}`);
    return false;
  }

  chat.ask(trimmed);
  return true;
}

// How long the assistant "thinks" before an answer shows, at the least - a
// reply that is there the instant the question is sent reads as canned.
const THINKING_MS = 1100;
const THINKING_JITTER_MS = 500;

const think = () => new Promise((resolve) => {
  window.setTimeout(resolve, THINKING_MS + Math.random() * THINKING_JITTER_MS);
});

/**
 * The assistant's answer as it wrote it: paragraphs split by a blank line,
 * lines starting with "- " as a list - a retelling has both; everything else
 * stays plain text.
 */
export function AnswerText({ text }) {
  return text.split(/\n{2,}/).map((block, i) => {
    const lines = block.split('\n');
    if (lines.every((line) => /^[-•*]\s+/.test(line))) {
      return (
        <ul key={i} className="search-chat-answer-list">
          {lines.map((line, j) => <li key={j}>{line.replace(/^[-•*]\s+/, '')}</li>)}
        </ul>
      );
    }
    return <p key={i} className="search-chat-answer">{block}</p>;
  });
}

/** The icon of an action: a Font Awesome class, or a page of its plugin. */
function ActionIcon({ action }) {
  const icon = action.card?.icon;
  if (!icon) {
    return <i className="fa-solid fa-arrow-up-right-from-square search-chat-option-icon search-chat-option-glyph" aria-hidden="true" />;
  }
  if (/^fa[a-z-]*\s|^fa-/.test(icon)) {
    return <i className={`${icon} search-chat-option-icon search-chat-option-glyph`} aria-hidden="true" />;
  }
  return <img src={pluginUrl(action.source, action.scope, icon)} alt="" className="search-chat-option-icon" />;
}

/**
 * Whether a message reads as a question or a request for the assistant rather
 * than a search term: it asks ("?"), or it is a sentence (four words or more).
 */
export function isQuestion(message) {
  const text = message.trim();
  return text.endsWith('?') || text.split(/\s+/).length >= 4;
}

/**
 * The questions sent to the assistant so far, each with its answer. `reply`
 * is what POST /assistant/ask answered - `{ answer, articles, action, degraded }`
 * - or `{ failed: true }`; `status` is 'loading' until it is in. Each question
 * is sent with the chat before it, so a follow-up ("in DePos") is understood.
 * `claims(message)` tells whether the chat wants a message before the search
 * gets it (see isQuestion; and every message once the chat has begun).
 */
export function useSearchConversation() {
  const [exchanges, setExchanges] = useState([]);
  const nextId = useRef(0);
  // The chat so far, for the next question; a ref, so two quick messages in a
  // row see each other.
  const history = useRef([]);

  const ask = useCallback(async (question) => {
    nextId.current += 1;
    const id = nextId.current;
    const update = (patch) => setExchanges((current) => current.map(
      (exchange) => (exchange.id === id ? { ...exchange, ...patch } : exchange),
    ));

    setExchanges((current) => [...current, { id, question, status: 'loading', reply: null }]);
    const earlier = history.current.slice(-4);
    history.current = [...history.current, { question, answer: '' }];
    const thinking = think();

    let reply;
    try {
      reply = await askAssistant(question, earlier);
    } catch {
      reply = { failed: true, answer: null, articles: [], action: null };
    }

    history.current = history.current.map((entry) => (
      entry.question === question && !entry.answer
        ? { question, answer: reply.answer || reply.articles?.map((a) => a.title).join('; ') || '' }
        : entry
    ));
    await thinking;
    update({ status: 'done', reply });
  }, []);

  const claims = useCallback(
    (message) => history.current.length > 0 || isQuestion(message),
    [],
  );

  return [exchanges, { ask, claims }];
}

/**
 * The chat with the knowledge assistant: the user's message, then the answer
 * (POST /assistant/ask) - its text, the articles it points to, and, when the
 * person wants to do something an application can do, a button for the action
 * a plugin offered, with the values they named (documentation/plugins.md §9).
 * The button runs it (POST /assistant/actions) and opens the page it answers.
 *
 * Without the model the answer is the search's articles alone (`degraded`),
 * and without any article it says nothing was found.
 *
 * The window is opened in the click itself, before the action runs: a browser
 * blocks a window opened after an await. It is pointed at the link once the
 * action answers, and closed again if it does not.
 */
export function SearchConversation({ exchanges, onNavigate }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
  // Per answer: 'starting', 'opened', or a refusal's message.
  const [handovers, setHandovers] = useState(() => new Map());
  const endRef = useRef(null);
  const base = 'ai-message';

  const handOver = async (id, action) => {
    const mark = (state) => setHandovers((current) => new Map(current).set(id, state));
    const win = window.open('', '_blank');
    mark({ state: 'starting' });
    try {
      const { url } = await runAssistantAction(action);
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.location.assign(url);
      }
      mark({ state: 'opened' });
    } catch (error) {
      win?.close();
      mark({ state: 'failed', message: error?.message });
    }
  };

  // Follow the conversation: scroll the chat box to its end when a message is
  // sent and again when the assistant's answer replaces "thinking" - only the
  // box, never the page behind the sheet.
  const progress = exchanges.map((exchange) => exchange.status).join();

  useEffect(() => {
    const box = endRef.current?.closest('.ai-chat-history');
    if (!box) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    box.scrollTo({ top: box.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [progress]);

  return (
    <>
      {exchanges.map(({ id, question, status, reply }) => {
        const done = status === 'done';
        const articles = done ? reply?.articles ?? [] : [];
        const action = done ? reply?.action : null;
        const disabled = pluginText(action?.card?.disabled, lang);
        const handover = handovers.get(id);
        let lead = null;
        if (done && articles.length > 0 && !reply?.answer && !action) {
          lead = t(reply?.degraded ? 'articleSearch.degradedReply' : 'articleSearch.suggestReply');
        } else if (done && !reply?.answer && !action) {
          lead = t(reply?.failed ? 'articleSearch.failedReply' : 'articleSearch.notFoundReply', { query: question });
        }

        return (
        <div key={id} className="search-chat-exchange">
          <div className={`${base} ${base}--user`}>
            <strong>{t('articleSearch.you')}</strong>
            <p>{question}</p>
          </div>

          <div className={`${base} ${base}--assistant`}>
            <strong>{t('aiSearch.assistantName')}</strong>
            {status === 'loading' && (
              <p className="search-chat-thinking" role="status">
                {t('articleSearch.thinking')}
                <span className="search-chat-typing" aria-hidden="true">
                  <span /><span /><span />
                </span>
              </p>
            )}
            {done && reply?.answer && <AnswerText text={reply.answer} />}
            {lead && <p>{lead}</p>}
            {action && (
              <>
                {action.fields.length > 0 && (
                  <dl className="search-chat-details">
                    {action.fields.map((field) => (
                      <div key={field.name}>
                        <dt>{field.label}</dt>
                        <dd>{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <div className="search-chat-options">
                  <button
                    type="button"
                    className="search-chat-option"
                    disabled={Boolean(disabled) || handover?.state === 'starting'}
                    title={disabled || undefined}
                    onClick={() => handOver(id, { source: action.source, key: action.key, params: action.params })}
                  >
                    <ActionIcon action={action} />
                    <span className="search-chat-option-text">
                      <small>{t('articleSearch.assistant')}</small>
                      <strong>
                        {pluginText(action.card?.title, lang)
                          || t('articleSearch.actionButton', { intent: action.intent, application: action.application })}
                      </strong>
                      {action.card?.hint && <span>{pluginText(action.card.hint, lang)}</span>}
                    </span>
                    <i className="fa-solid fa-arrow-right search-chat-option-arrow" aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
            {articles.length > 0 && (
              <>
                {reply?.answer && <p className="search-chat-sources">{t('articleSearch.sources')}</p>}
                <ul className="search-chat-suggestions">
                  {articles.map((article) => (
                    <li key={article.article_id}>
                      <ArticleLink article={article} className="article-search-item" onClick={onNavigate}>
                        <ArticleIcon
                          icon={article.icon}
                          seed={article.title}
                          className="article-search-icon"
                          fallback="fa-solid fa-file-lines"
                        />
                        <span className="article-search-text">
                          <strong>{article.title}</strong>
                          {article.summary && <span>{article.summary}</span>}
                        </span>
                        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                      </ArticleLink>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {disabled && (
              <p className="article-search-status" role="status">{disabled}</p>
            )}
            {handover && (
              <p className="article-search-status" role="status">
                {handover.state === 'starting' && t('articleSearch.actionStarting')}
                {handover.state === 'opened' && t('articleSearch.actionOpened')}
                {handover.state === 'failed' && t('articleSearch.actionFailed', { message: handover.message || '' })}
              </p>
            )}
          </div>
        </div>
        );
      })}
      <div ref={endRef} />
    </>
  );
}

/**
 * The hits for `query`: matching tags, then article links. Nothing at all is
 * shown when nothing matches - pressing "Fragen" then asks the assistant
 * (see submitSearch).
 */
function ArticleSearchResults({ query, status, results, tags = [], onNavigate, className = '' }) {
  const { t } = useTranslation();

  if (status === 'idle') return null;

  const hasResults = results.length > 0;
  const hasTags = tags.length > 0;

  return (
    <div className={`article-search-results ${className}`} aria-live="polite">
      {hasTags && (
        <div className="article-search-tags" aria-label={t('articleSearch.tagsAriaLabel')}>
          <span className="article-search-label">{t('articleSearch.tags')}</span>
          {tags.map((tag) => (
            <Link
              key={tag.name}
              to={`/tag/${encodeURIComponent(tag.name)}`}
              className="article-search-tag"
              onClick={onNavigate}
            >
              <i className="fa-solid fa-tag" aria-hidden="true" />
              <Highlight text={tag.name} query={query} />
              <span className="article-search-count">{tag.count}</span>
            </Link>
          ))}
        </div>
      )}

      {hasResults && (
        <ul className="article-search-list" aria-label={t('articleSearch.resultsAriaLabel')}>
          {results.map((article) => (
            <li key={article.article_id}>
              <ArticleLink article={article} className="article-search-item" onClick={onNavigate}>
                <ArticleIcon
                  icon={article.icon}
                  seed={article.title}
                  className="article-search-icon"
                  fallback="fa-solid fa-file-lines"
                />
                <span className="article-search-text">
                  <strong><Highlight text={article.title} query={query} /></strong>
                  {article.snippet
                    ? <Snippet text={article.snippet} />
                    : article.summary && <span>{article.summary}</span>}
                  {article.tags?.length > 0 && (
                    <span className="article-search-item-tags">
                      {article.tags.map((tag) => (
                        <em key={tag}><Highlight text={tag} query={query} /></em>
                      ))}
                    </span>
                  )}
                </span>
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </ArticleLink>
            </li>
          ))}
        </ul>
      )}

      {status === 'error' && (
        <p className="article-search-status">{t('articleSearch.error')}</p>
      )}

    </div>
  );
}

export default ArticleSearchResults;
